import { useNavigate } from "react-router-dom";

const RAM_LOGO =
  "https://customer-assets-lqy194kg.emergentagent.net/job_novelty-hub-5/artifacts/ojdo80ez_Ram%20Shack%20Logo.JPG";

const Home = () => {
  const navigate = useNavigate();

  const enterVilla = () => {
    // Keep the flag so VillaAmbient + Rooms know the threshold has been
    // crossed at least once (used for audio-gesture routing), but we no
    // longer auto-redirect past the splash — the ritual repeats every visit.
    localStorage.setItem("rs_villa_entered", "1");
    // Fire a same-tab event so components like VillaAmbient can react
    // synchronously — this click is a valid user-gesture for AudioContext.
    window.dispatchEvent(new Event("rs-villa-entered"));
    navigate("/rooms");
  };

  return (
    <div
      data-testid="home-splash"
      className="relative min-h-screen overflow-hidden flex items-center justify-center"
      // Pure black stage so the ram JPG's black square disappears seamlessly
      // when the image is composited with mix-blend-mode: screen below.
      style={{ background: "#000000" }}
    >
      {/* Vignette floor — pulls the eye back to center */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 55%)",
        }}
      />

      {/* Centered stack */}
      <div className="relative z-10 flex flex-col items-center px-6 max-w-[720px] w-full text-center fade-up">
        {/* SEO-only content — visible to crawlers, hidden from sighted users so
            the cinematic splash stays uncluttered. Provides 300+ words of
            meaningful copy about the brand, rooms, and product categories. */}
        <div className="sr-only">
          <h1>Ram Shack — a discreet online luxury boutique for adult toys, lingerie, and intimacy wellness</h1>
          <p>
            Welcome to Ram Shack, an online boutique for tasteful adult
            indulgences styled after a gothic villa on the Aegean sea. Every
            order ships in plain, unbranded packaging with no reference to the
            store name on the label — your privacy is the standard, not an
            upgrade. We work with vetted wholesalers including Lifestyle
            Distributing, Eldorado Trading Company, and Honey&apos;s Place to
            bring you a curated catalogue of luxury sex toys, silk and lace
            lingerie, botanical intimacy wellness, and thoughtfully-designed
            accessories.
          </p>
          <h2>Explore the villa by room</h2>
          <p>
            Our storefront is arranged as a floor plan of rooms rather than a
            traditional category grid. Slip into the{" "}
            <a href="/shop/basement">Basement</a> for playful indulgences and
            after-dark novelties, the <a href="/shop/bedroom">Bedroom</a> for
            silks, lace, and satin lingerie, the{" "}
            <a href="/shop/apothecary">Apothecary</a> for arousal serums, warming
            oils, and botanical wellness elixirs, and the{" "}
            <a href="/shop/rooftop">Rooftop</a> for hand-blown glass, ceramic
            keepsakes, and objects of art. Every room has its own bespoke ambient
            audio and gothic photography.
          </p>
          <h2>Curated collections</h2>
          <p>
            Beyond the rooms, browse our category collections:{" "}
            <a href="/shop/toys">Toys</a>, <a href="/shop/lingerie">Lingerie</a>,{" "}
            <a href="/shop/wellness">Wellness</a>, and{" "}
            <a href="/shop/accessories">Accessories</a>. Read the{" "}
            <a href="/journal">Villa Journal</a> for a monthly memoir from the
            house — a ritual, a recommendation, and something the house has been
            thinking about, delivered discreetly to your inbox. Meet the AI{" "}
            <a href="/rooms">Concierge</a> for a private consultation — she only
            recommends products currently in stock, never fabricated ones.
          </p>
          <h2>Discretion, wellness, and quality</h2>
          <p>
            Ram Shack is age-gated at 18+ and every visit begins with a
            verification. Payments are processed securely through Stripe. Returns
            follow our discreet 30-day policy for unopened wellness items. Our
            vendor partners are established distributors of premium adult brands
            including Doc Johnson, Pipedream, California Exotics, and Topco —
            you&apos;re getting the same manufacturer inventory as the finest
            adult boutiques, in a shopping experience that feels more like a
            visit to a private villa than a warehouse aisle.
          </p>
        </div>

        <div className="overline mb-8" style={{ color: "var(--rs-terracotta)" }}>
          Est. 2026 &nbsp;·&nbsp; Adults Only &nbsp;·&nbsp; Discretion Assured
        </div>

        {/* Ram logo — the aura is CONTAINED inside the logo's own frame so the
            light lives BETWEEN the black square and the ram, not beyond it.
            mix-blend-mode: screen on the JPG makes its pure-black background
            invisible so the aura shines through the negative space around the
            ram silhouette. */}
        <div
          className="relative w-[68vw] max-w-[520px] aspect-square logo-breathe"
        >
          {/* Aura core — soft amber warmth, kept low so the ram detail is
              never blown out. Sits behind the ram artwork. */}
          <div
            aria-hidden
            className="absolute inset-0 aura-core pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 50% 55%, rgba(220,150,80,0.28) 0%, rgba(180,90,40,0.16) 18%, rgba(90,35,20,0.08) 38%, rgba(0,0,0,0) 62%)",
            }}
          />
          {/* Aura halo — even softer outer bleed so the edge just breathes */}
          <div
            aria-hidden
            className="absolute inset-0 aura-halo pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 50% 52%, rgba(230,175,110,0.10) 0%, rgba(150,70,45,0.05) 40%, rgba(0,0,0,0) 72%)",
            }}
          />
          <img
            src={RAM_LOGO}
            alt="Ram Shack"
            className="relative w-full h-full object-contain"
            style={{ mixBlendMode: "screen" }}
          />
        </div>

        <div
          className="font-serif-display text-xl sm:text-2xl text-bone mt-6 tracking-[0.35em] uppercase"
          style={{ letterSpacing: "0.35em" }}
        >
          Discipline &nbsp;·&nbsp; Ritual &nbsp;·&nbsp; Desire
        </div>

        <p className="text-muted-bone max-w-md text-sm sm:text-base leading-relaxed mt-8 mb-10">
          Cross the threshold. Choose the room the night calls you toward.
        </p>

        {/* Beckoning button — an eerie crimson pulse throbs BEHIND the CTA
            like a distant candle in a dark hallway calling you forward. */}
        <div className="relative flex items-center justify-center">
          {/* Outer breath — wide, slow, warm halo that fades and returns */}
          <div
            aria-hidden
            className="absolute w-[420px] h-[220px] beckon-outer pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at 50% 50%, rgba(200,40,50,0.35) 0%, rgba(120,15,25,0.18) 35%, rgba(0,0,0,0) 68%)",
              filter: "blur(6px)",
            }}
          />
          {/* Inner heartbeat — tighter, hotter pulse hugging the button */}
          <div
            aria-hidden
            className="absolute w-[300px] h-[130px] beckon-inner pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at 50% 50%, rgba(230,80,80,0.55) 0%, rgba(150,20,30,0.28) 40%, rgba(0,0,0,0) 75%)",
              filter: "blur(2px)",
            }}
          />
          <button
            type="button"
            data-testid="hero-enter-villa-btn"
            onClick={enterVilla}
            className="group relative overflow-hidden px-14 py-5 uppercase text-xs sm:text-sm tracking-[0.36em] text-bone transition-all duration-500 cursor-pointer villa-cta"
            style={{
              background:
                "linear-gradient(180deg, rgba(120,20,30,0.9) 0%, rgba(65,10,20,0.95) 100%)",
              border: "1px solid rgba(210,150,110,0.35)",
              boxShadow:
                "0 0 40px rgba(120,20,30,0.35), inset 0 0 20px rgba(0,0,0,0.4)",
            }}
          >
            <span className="relative z-10">Enter the Villa</span>
            <span
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background:
                  "linear-gradient(180deg, rgba(170,50,60,0.95) 0%, rgba(90,15,25,0.95) 100%)",
              }}
            />
          </button>
        </div>

        <div className="mt-10 text-[10px] uppercase tracking-[0.4em] text-muted-bone/60">
          Plain paper &nbsp;·&nbsp; Encrypted checkout &nbsp;·&nbsp; No trace
        </div>
      </div>

      <style>{`
        @keyframes logo-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.015); }
        }
        .logo-breathe { animation: logo-breathe 6s ease-in-out infinite; }

        @keyframes aura-core {
          0%, 100% { opacity: 0.75; transform: scale(1); }
          50%      { opacity: 1;    transform: scale(1.02); }
        }
        .aura-core { animation: aura-core 7s ease-in-out infinite; }

        @keyframes aura-halo {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(1.04); }
        }
        .aura-halo { animation: aura-halo 10s ease-in-out infinite; }

        .villa-cta:hover {
          transform: translateY(-1px);
          box-shadow: 0 0 60px rgba(180,50,60,0.55), inset 0 0 25px rgba(0,0,0,0.35);
        }

        /* Beckoning glow — irregular, two-layer pulse so it feels like
           an unquiet flame calling from a dark hallway. Different periods
           on the two layers keeps the rhythm eerie rather than mechanical. */
        @keyframes beckon-outer {
          0%, 100% { opacity: 0.55; transform: scale(1);    }
          40%      { opacity: 0.95; transform: scale(1.08); }
          70%      { opacity: 0.35; transform: scale(0.96); }
        }
        .beckon-outer { animation: beckon-outer 3.4s ease-in-out infinite; }

        @keyframes beckon-inner {
          0%, 100% { opacity: 0.7;  transform: scale(1);    }
          35%      { opacity: 1;    transform: scale(1.12); }
          65%      { opacity: 0.5;  transform: scale(0.94); }
        }
        .beckon-inner { animation: beckon-inner 2.2s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default Home;
